/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 25/09/2024 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Estabilización de selección de vendedor
*      3       | 05/06/2026 | Lic. José Luis Aragón Cervantes  | Limpieza de datos al cerrar firma
* --------------------------------------------------------------------------------------- */

import TextField from "../../ui/TextField";
import Autocomplete from "../../ui/Autocomplete";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiAccesosList } from "../../../settings/ApiConfig";

import { Button, Box, InputAdornment, Grid } from "@mui/material";
import PersonIcon from "@mui/icons-material/PersonOutlineSharp";
import KeyIcon from "@mui/icons-material/LockOutlined";

import { useRequest } from "../../../context/System/RequestContext";
import { useLayout } from "../../../context/System/LayoutContext";

import SizeSoftXModalPortatil from "../leyout/SizeSoftXModalPortatil";



export default function ModalFirmaVentas(props) {
  const { openModalFirmaPreVentas, handleCloseModalFirmaPreVentas, handleFirmarPreventa } = props;

  const { GetRequest } = useRequest();
  const { handleOpenAlert } = useLayout();

  // data
  const [listaVendedores, setListaVendedores] = useState([]);
  const [loadingVendedores, setLoadingVendedores] = useState(false);

  // form
  const [vendedorSeleccionado, setVendedorSeleccionado] = useState(null);
  const [vendedorInput, setVendedorInput] = useState("");
  const [passwordString, setPasswordString] = useState("");
  const [passwordFocused, setPasswordFocused] = useState(false);

  // refs
  const vendedorInputRef = useRef(null);
  const passwordRef = useRef(null);

  const resetFirmaForm = useCallback(() => {
    setVendedorSeleccionado(null);
    setVendedorInput("");
    setPasswordString("");
  }, []);

  const handleCloseFirma = useCallback(() => {
    resetFirmaForm();
    handleCloseModalFirmaPreVentas();
  }, [handleCloseModalFirmaPreVentas, resetFirmaForm]);

  // ======= Reset + cargar vendedores al abrir =======
  useEffect(() => {
    if (!openModalFirmaPreVentas) return;

    resetFirmaForm();

    // foco al abrir (al input del autocomplete)
    setTimeout(() => {
      vendedorInputRef.current?.focus?.();
    }, 0);

    // cargar solo si no hay
    if (listaVendedores.length > 0 || loadingVendedores) return;

    (async () => {
      try {
        setLoadingVendedores(true);
        const response = await GetRequest({
          url: apiAccesosList,
          loader: "Cargando lista vendedores del sistema",
        });

        if (response?.hasError) {
          handleOpenAlert(response?.message ?? "Error al cargar vendedores", "error");
          return;
        }

        setListaVendedores(response?.result ?? []);
      } finally {
        setLoadingVendedores(false);
      }
    })();
  }, [openModalFirmaPreVentas, GetRequest, handleOpenAlert, listaVendedores.length, loadingVendedores, resetFirmaForm]);

  const validarVendedores = (id, clave) => {
    if (!id || !clave) return false;

    const found = listaVendedores.find(
      (e) =>
        e.id === id &&
        (e.clavesecreta ?? "").trim().toUpperCase() === clave.trim().toUpperCase()
    );

    return !!found;
  };

  const handleValidarFirmaPreventa = () => {
    const vendedorId = vendedorSeleccionado?.id ?? 0;
    const pass = (passwordString ?? "").trim();

    if (!vendedorId || pass === "") {
      handleOpenAlert("Debe seleccionar un vendedor e ingresar la firma.", "error");
      return;
    }

    const ok = validarVendedores(vendedorId, pass);

    if (!ok) {
      handleOpenAlert("Firma incorrecta. Intente de nuevo.", "error");
      return;
    }

    const nomvend = listaVendedores.find((e) => e.id === vendedorId);
    handleFirmarPreventa((nomvend?.nombreusuario ?? "").trim());
    handleCloseFirma();
  };

  // ======= Teclas: Enter firma, excepto si estás eligiendo opción del Autocomplete =======
  const handleKeyDownModal = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      handleCloseFirma();
      return;
    }

    // Si estás navegando dentro del Autocomplete, dejamos que MUI confirme con flechas + Enter.
    if (event.target?.closest?.(".MuiAutocomplete-root")) return;

    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      handleValidarFirmaPreventa();
    }
  };

  const handleKeyDownPassword = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      handleValidarFirmaPreventa();
    }
  };
    
  return (
    <SizeSoftXModalPortatil
            open={openModalFirmaPreVentas}
            onClose={handleCloseFirma}
            aria-labelledby="menu-modal-title"
            aria-describedby="menu-modal-description"
        >
            <div onKeyDown={handleKeyDownModal}>
        <div className="containerFirma">
          <Autocomplete
            sx={{ width: "240px" }}
            options={listaVendedores}
            loading={loadingVendedores}
            value={vendedorSeleccionado}
            inputValue={vendedorInput}
            autoHighlight
            openOnFocus
            selectOnFocus
            handleHomeEndKeys
            clearOnBlur={false}
            blurOnSelect={false}
            disablePortal
            noOptionsText="Sin vendedores"
            loadingText="Cargando vendedores..."
            onInputChange={(event, newInputValue, reason) => {
              setVendedorInput(newInputValue);
              if (reason === "input" || reason === "clear") {
                setVendedorSeleccionado(null);
              }
            }}
            onChange={(event, newValue) => {
              setVendedorSeleccionado(newValue);
              setVendedorInput(newValue?.nombreusuario ?? "");
              if (newValue) {
                setTimeout(() => passwordRef.current?.focus?.(), 0);
              }
            }}
            getOptionLabel={(option) => option?.nombreusuario ?? ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vendedor:"
                inputRef={vendedorInputRef}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </div>

        <div className="containerPassword">
          <TextField
            inputRef={passwordRef}
            name="password"
            label="Firma:"
            value={passwordString}
            onChange={(e) => setPasswordString(e.target.value)}
            onKeyDown={handleKeyDownPassword}
            type="password"
            autoComplete="off"
            readOnly={!passwordFocused}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
            sx={{ width: "240px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <Grid textAlign="center" mt={2}>
          <Button
            className="validarFirmaButton"
            style={{ width: "240px" }}
            variant="contained"
            onClick={handleValidarFirmaPreventa}
            disabled={loadingVendedores}
          >
            Firmar preventa
          </Button>
        </Grid>
            </div>
        </SizeSoftXModalPortatil>
  );
}


